// Epic 79822 — PostIF list + approval service (the list-enrichment, mark-as-employee, and
// approver-review functions). Kept in its own module (separate from pifService.ts, which holds
// Epic 79820's form-wiring) so both epics' DTOs coexist without name/type collisions. pifService
// re-exports the symbols the list page and approval screens import.
import {
  PifApprovalAuditEntry,
  PifApprovalChainEntry,
  PifApprovalDisplayRow,
  PifApprovalRecord,
  PifApprovalRoleMeta,
  PifApproverRole,
  PifPositionMeta,
  PifApprovalStep,
  PifApprovalStepState,
  PifListItem,
  PifListFlow,
  PifListStatus,
  PifListEmploymentType,
} from "../pages/supervision/PostInterviewForm/types";
import {
  PostIfHrListResultDto,
  PostIfListItemDto,
  PostIfMyListResultDto,
  PostIfResultDto,
} from "../types/pifListDto";
import { PostIfLookupItem, PostIfPositionRules } from "../types/pifReferenceDto";
import { PostIfDto, PostIfSignatureDto } from "../types/pifApprovalDto";
import apiClient from "../utils/apiClient";
import { readPostIfWrite } from "./pifWriteResult";
import { toDisplayDateOrNull } from "../utils/dateHelper";

/* ── PIF Approval — the screens' static role vocabulary (labels and CTAs).
 *
 * NOT stub data: the record itself is live, assembled in assemblePifApprovalRecord below from
 * GET /postif/{id} (header, applicant, answers and the signature timeline), with approve/reject
 * posting to /postif/approve and /postif/reject. Only the wording each role sees lives here.
 * The header this replaces still said "stub data, until the approval-chain API exists", which
 * outlived the wiring and read as if the whole screen were fake (PR 30856 review, Pooja Pujara). */

export const APPROVAL_ROLES: Record<PifApproverRole, PifApprovalRoleMeta> = {
  dept: {
    label: "Department Director",
    order: 2,
    signCta: "Sign and send to HR",
    forward: "HR Director",
    // Role, not a person: no endpoint hands this screen the next signer's name (the chain rail
    // shows role labels for the same reason), and the invented names that sat here would have
    // shipped on the receipt of every signed form.
    receiptNext: "HR Director",
  },
  hr: {
    label: "HR Director",
    order: 3,
    signCta: "Approve and send to ED",
    forward: "Executive Director",
    receiptNext: "Executive Director",
  },
  exec: {
    label: "Executive Director",
    order: 4,
    signCta: "Sign — final approval",
    forward: null,
    receiptNext: "Onboarding and payroll setup",
  },
};

// API signature role → the approver role vocabulary the screens use (manager isn't an approver).
const API_ROLE_TO_APPROVER: Record<string, PifApproverRole> = {
  "dept-director": "dept",
  "department-director": "dept",
  "hr-director": "hr",
  "executive-director": "exec",
};

const SIGNATURE_ROLE_LABEL: Record<string, string> = {
  manager: "Manager (creator)",
  "dept-director": "Department Director",
  "department-director": "Department Director",
  "hr-director": "HR Director",
  "executive-director": "Executive Director",
};

const APPROVAL_FLOW_TAG: Record<string, string> = {
  "new-hire": "New hire",
  "existing-active": "Internal transfer",
  "existing-inactive": "Rehire",
};

const approvalStamp = (utc: string | null, ip?: string | null): string => {
  const date = toDisplayDateOrNull(utc);
  if (!date) return "";
  const when = date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  return ip ? `${when} · ${ip}` : when;
};

/** Resolved id → display names the assembler can't get from the form read alone. */
export interface PifApprovalLookups {
  departmentName?: string;
  positionName?: string;
  rateTypeName?: string;
  reportsToName?: string;
  serviceName?: string;
  clientName?: string;
}

/** Pure assembler: turns the real form read (+ position rules + resolved names) into the rich
 * PifApprovalRecord the review screens render. Kept separate from the fetch so it is unit-testable. */
export const assemblePifApprovalRecord = (
  dto: PostIfDto,
  rules: PostIfPositionRules | null,
  currentUserId: number | null,
  lookups: PifApprovalLookups = {},
): PifApprovalRecord => {
  const { header, applicant } = dto;
  const answers = new Map(dto.answers.map((a) => [a.fieldKey, a.value ?? ""]));
  const answer = (key: string): string => answers.get(key) ?? "";

  const signatures = [...dto.signatures].sort((a, b) => (a.sortVal ?? 0) - (b.sortVal ?? 0));
  // A form is only awaiting signatures while its Status is 'submitted'. A rejection stamps ONLY
  // the rejecting row, so every downstream row is still Signed IS NULL — taking the earliest of
  // those unconditionally handed the NEXT director a live turn on a form that had just been
  // returned to its supervisor ('changes_requested') or declined outright. The chain is not
  // cleared and re-laid until the supervisor resubmits (sProc_Portal_PostIF_Submit_U), so until
  // then nobody's turn is live. sProc_Portal_PostIFSignatures_U enforces the same rule
  // server-side and answers 409, so this is the visible half of a two-sided gate, not the gate.
  //
  // With `pending` undefined everything downstream follows: no chain row is "current",
  // viewerIsCurrentSigner is false, the decision panel is replaced by the read-only note, and the
  // topbar chip reads "View only". The status itself stays visible in headerSub.
  const awaitingSignatures = (header.status ?? "").trim().toLowerCase() === "submitted";
  const pending = awaitingSignatures ? signatures.find((s) => s.signed === null) : undefined;
  const currentRole: PifApproverRole =
    (pending?.role && API_ROLE_TO_APPROVER[pending.role.toLowerCase()]) || "dept";
  const viewerIsCurrentSigner = pending?.userId != null && pending.userId === currentUserId;

  const chain: PifApprovalChainEntry[] = signatures.map((s) => {
    const roleKey = (s.role ?? "").toLowerCase();
    const isPending = s === pending;
    const state = s.signed === null ? (isPending ? "current" : "waiting") : "done";
    const detail =
      s.signed === true
        ? s.reason || "Signed"
        : s.signed === false
          ? `Returned${s.reason ? `: ${s.reason}` : ""}`
          : isPending
            ? "Waiting on this signature"
            : "Not started yet";
    return {
      name: SIGNATURE_ROLE_LABEL[roleKey] ?? s.role ?? "Signer",
      role: SIGNATURE_ROLE_LABEL[roleKey] ?? s.role ?? "",
      detail,
      stamp: s.signed !== null ? approvalStamp(s.signedUtc, s.ipAddress) : "",
      state,
    };
  });

  const audit: PifApprovalAuditEntry[] = signatures
    .filter((s) => s.signed !== null)
    .map((s) => {
      const label = SIGNATURE_ROLE_LABEL[(s.role ?? "").toLowerCase()] ?? s.role ?? "Signer";
      return { what: `${label} ${s.signed ? "signed" : "returned for changes"}`, when: approvalStamp(s.signedUtc, s.ipAddress) };
    });

  const rateNum = parseFloat(answer("requested_rate"));
  const hasRate = Number.isFinite(rateNum);
  const meta = toPifPositionMeta(rules);
  const hiringMin = meta ? meta.hiring[0] : 0;
  const hiringMax = meta ? meta.hiring[1] : 0;
  const hasBand = !!meta && (hiringMin > 0 || hiringMax > 0);
  const outOfBand = hasRate && hasBand && (rateNum < hiringMin || rateNum > hiringMax);

  const applicantName = `${applicant?.firstName ?? ""} ${applicant?.lastName ?? ""}`.trim() || "Applicant";
  const applicantInitials = `${applicant?.firstName?.[0] ?? ""}${applicant?.lastName?.[0] ?? ""}`.toUpperCase() || "+";
  const startDate = toDisplayDateOrNull(answer("desired_start_date"));
  const startDateText = startDate ? startDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  const employeeId = header.enterpriseEmployeeId ?? applicant?.enterpriseEmployeeId ?? null;

  const dash = (v: string | null | undefined): string => (v && v.trim() ? v : "—");
  const addressLine = [applicant?.address1, applicant?.city, applicant?.state, applicant?.zip].filter(Boolean).join(", ");

  const rateRow: PifApprovalDisplayRow = { k: "Rate", v: hasRate ? `$${rateNum.toFixed(2)}` : "—" };
  if (hasRate && hasBand) {
    rateRow.pill = outOfBand ? "Outside hiring band" : "Inside band";
    rateRow.pillTone = outOfBand ? "warn" : "ok";
  }
  const compensationRows: PifApprovalDisplayRow[] = [
    rateRow,
    { k: "Rate type", v: dash(lookups.rateTypeName) },
    { k: "Hours per week", v: dash(answer("hours_per_week")) },
  ];

  const sections: PifApprovalRecord["sections"] = [
    {
      title: "Applicant",
      note: "Read-only · from the form",
      rows: [
        { k: "Full name", v: applicantName },
        { k: "Social Security Number", v: dash(applicant?.ssn), pill: "Masked" },
        { k: "Employee ID", v: employeeId != null ? String(employeeId) : "New applicant" },
        { k: "Status", v: dash(applicant?.status) },
      ],
    },
    {
      title: "Contact",
      note: "Read-only",
      rows: [
        { k: "Address", v: dash(addressLine) },
        { k: "Cell", v: dash(applicant?.cell) },
        { k: "Home", v: dash(applicant?.homePhone) },
        { k: "Email", v: dash(applicant?.email) },
      ],
    },
    {
      title: "Position and assignment",
      note: "Read-only",
      rows: [
        { k: "Department", v: dash(lookups.departmentName) },
        { k: "Position", v: dash(lookups.positionName) },
        ...(meta?.cls ? [{ k: "Classification", v: meta.cls }] : []),
        { k: "Reports to", v: dash(lookups.reportsToName) },
        ...(lookups.serviceName ? [{ k: "Service", v: lookups.serviceName }] : []),
        ...(lookups.clientName ? [{ k: "Individual / client", v: lookups.clientName }] : []),
        { k: "Start date", v: startDateText },
      ],
    },
    {
      title: "Compensation and schedule",
      note: "Read-only",
      rows: compensationRows,
      ...(hasBand
        ? {
            band: {
              positionMin: meta ? meta.band[0] : 0,
              positionMax: meta ? meta.band[1] : 0,
              hiringMin,
              hiringMax,
              rate: hasRate ? rateNum : 0,
              outOfBand,
            },
          }
        : {}),
    },
  ];

  return {
    postIfId: header.postIfId,
    pifId: header.refNo ?? `PIF-${header.postIfId}`,
    currentRole,
    applicantName,
    applicantInitials,
    positionTag: (header.flow && APPROVAL_FLOW_TAG[header.flow.toLowerCase()]) || "",
    applicantMeta: [lookups.positionName, lookups.departmentName, hasRate ? `$${rateNum.toFixed(2)}/hr` : null, `starts ${startDateText}`]
      .filter(Boolean)
      .join(" · "),
    headerSub: `${header.refNo ?? `PIF-${header.postIfId}`} · ${dash(header.status)}`,
    employeeIdLabel: employeeId != null ? `Employee ID ${employeeId}` : "New applicant",
    viewerIsCurrentSigner,
    chain,
    audit,
    sections,
  };
};

/** Who returned a form for changes, why, and when - the banner a supervisor needs on reopen. */
export interface PifReturnNote {
  by: string;
  reason: string;
  when: string;
}

/**
 * The rejection note for a form currently sitting in 'changes_requested'.
 *
 * Gated on the header status, not merely on "a rejected row exists": once the supervisor
 * re-submits, sProc_Portal_PostIF_Submit_U soft-deletes the whole prior chain, so a live payload
 * cannot carry a stale rejection - but keying off the status makes that intent explicit rather
 * than relying on the read to have filtered it. Highest sortVal wins; only one rejected row can
 * exist in a live chain, since a rejection halts it.
 *
 * Lives here beside the chain mapping so the role labels and the EST stamp have one source.
 *
 * Takes a structural parameter rather than a PostIfDto because this branch has two of them -
 * types/pifDto (what usePifDraft.load returns, no signatures declared) and types/pifApprovalDto
 * (what the approval screen reads). Both hit GET /postif/{id}, which does return the timeline, so
 * the field is present at runtime either way; declaring only what this needs works with both.
 */
export const derivePifReturnNote = (dto: {
  header: { status?: string | null };
  signatures?: PostIfSignatureDto[] | null;
}): PifReturnNote | null => {
  if ((dto.header.status ?? "").trim().toLowerCase() !== "changes_requested") return null;

  const returned = [...(dto.signatures ?? [])]
    .filter((s) => s.signed === false)
    .sort((a, b) => (b.sortVal ?? 0) - (a.sortVal ?? 0))[0];
  if (!returned) return null;

  return {
    by: SIGNATURE_ROLE_LABEL[(returned.role ?? "").toLowerCase()] ?? returned.role ?? "an approver",
    reason: (returned.reason ?? "").trim(),
    when: approvalStamp(returned.signedUtc),
  };
};

// Orchestrates the approver review: resolve the deep-link token (or numeric id) → load the form,
// its position rules, and the id→name lookups → assemble the record. Returns null when the link
// can't be opened (expired/unknown token or the record isn't readable).
export const getPifApproval = async (lookupKey: string, currentUserId: number | null): Promise<PifApprovalRecord | null> => {
  const postIfId = /^\d+$/.test(lookupKey) ? Number(lookupKey) : await resolvePifApprovalToken(lookupKey);
  if (!postIfId) return null;

  let dto: PostIfDto;
  try {
    dto = await getPif(postIfId);
  } catch {
    return null;
  }

  const { header } = dto;
  const answerValue = (key: string): string | null => dto.answers.find((a) => a.fieldKey === key)?.value ?? null;
  const deptId = header.departmentId;
  const posId = header.deptPositionId;
  const serviceId = Number(answerValue("service"));
  const nameById = (list: PostIfLookupItem[], id: string | number | null): string | undefined =>
    id == null || id === "" ? undefined : list.find((x) => String(x.id) === String(id))?.text ?? undefined;

  const [departments, positions, rateTypes, reportToUsers, services, clients, rules] = await Promise.all([
    getPifDepartments().catch(() => []),
    deptId ? getPifPositions(deptId).catch(() => []) : Promise.resolve([]),
    getPifRateTypes().catch(() => []),
    deptId ? getPifReportToUsers(deptId).catch(() => []) : Promise.resolve([]),
    deptId ? getPifServices(deptId).catch(() => []) : Promise.resolve([]),
    Number.isFinite(serviceId) && serviceId > 0 ? getPifClients(serviceId).catch(() => []) : Promise.resolve([]),
    deptId && posId ? getPifPositionRules(deptId, posId).catch(() => null) : Promise.resolve(null),
  ]);

  return assemblePifApprovalRecord(dto, rules, currentUserId, {
    departmentName: nameById(departments, deptId),
    positionName: nameById(positions, posId),
    rateTypeName: nameById(rateTypes, answerValue("rate_type")),
    reportsToName: nameById(reportToUsers, answerValue("reports_to")),
    serviceName: nameById(services, answerValue("service")),
    clientName: nameById(clients, answerValue("client")),
  });
};

/* ── PIF list — real data via GET /api/postif/my-list and /hr-list (Epic 79822) ── */

const APPROVAL_STEP_LABELS = ["Manager (creator)", "Dept Director", "HR Director", "Exec Director"];

const APPROVAL_STEP_DETAILS: Record<PifApprovalStepState, string> = {
  signed: "Signed · attested",
  pending: "Pending",
  declined: "Declined",
  returned: "Returned for changes",
};

const chain = (states: PifApprovalStepState[]): PifApprovalStep[] =>
  APPROVAL_STEP_LABELS.map((label, index) => {
    const state = states[index] ?? "pending";
    return { label, state, detail: APPROVAL_STEP_DETAILS[state] };
  });

// The API list rows are id-based; the display names (applicant / department / position /
// employment type) arrive via the pending list-DTO enrichment and stay blank until it lands.
// Signature staging (status chip + approval dots) is derived from `status` plus the row's current
// signature stage, which both list endpoints now return — the HR list only since Bug 79824, before
// which its rows had no stage at all and every submitted one rendered as awaiting the first
// signature. Still not the full per-role pipeline: only the stage the chain is sitting on is real,
// so the dots before it are inferred as signed rather than read row by row.

const FLOW_LABELS: Record<string, PifListFlow> = {
  "new-hire": "New",
  "existing-active": "Existing·Active",
  "existing-inactive": "Existing·Inactive",
};

// Portal_PostIFSignatures.Role → 0-based step in APPROVAL_STEP_LABELS.
const ROLE_STEP: Record<string, number> = {
  manager: 0,
  "dept-director": 1,
  "department-director": 1,
  "hr-director": 2,
  "executive-director": 3,
};

const mapFlow = (flow: string | null): PifListFlow => FLOW_LABELS[(flow ?? "").toLowerCase()] ?? "New";

// Enriched employmentType may be absent (blank until the API lands) or arrive as "Full-time"/
// "Part-time" or "FT"/"PT"; anything part-time-shaped is PT, everything else defaults to FT.
const mapEmploymentType = (value?: string | null): PifListEmploymentType => {
  const v = (value ?? "").toLowerCase();
  return v === "pt" || v.includes("part") ? "PT" : "FT";
};

const formatUpdated = (iso: string | null): string => {
  const date = toDisplayDateOrNull(iso);
  return date ? date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "";
};

// The pending stage: the current signature stage, advanced by one when that stage is already signed.
const pendingStep = (role?: string | null, signed?: boolean | null): number => {
  const step = ROLE_STEP[(role ?? "").toLowerCase()];
  if (step === undefined) return 1; // submitted but the API sent no stage → first review stage
  return signed ? step + 1 : step;
};

export const derivePifListStatus = (dto: PostIfListItemDto): PifListStatus => {
  if (dto.enterpriseEmployeeId) return "APPROVED"; // marked-as-employee is the only terminal signal today
  const status = (dto.status ?? "").toLowerCase();
  if (status === "draft") return "DRAFT";
  // 'changes_requested' is an ACTIONABLE state, not a terminal one: sProc_Portal_PostIF_Submit_U
  // accepts it and the attribute/applicant IU procs still allow writes. Collapsing it into
  // DECLINED made the supervisor's own returned form look dead and disabled its Edit action.
  if (status === "changes_requested") return "RETURNED";
  if (status === "declined") return "DECLINED";
  if (status === "submitted") {
    const step = pendingStep(dto.latestSignatureRole, dto.latestSignatureSigned);
    if (step <= 1) return "PENDING DEPT DIR";
    if (step === 2) return "PENDING HR DIR";
    return "PENDING EXEC DIR";
  }
  return "DRAFT";
};

export const derivePifApprovals = (dto: PostIfListItemDto): PifApprovalStep[] => {
  const status = (dto.status ?? "").toLowerCase();
  if (dto.enterpriseEmployeeId) return chain(["signed", "signed", "signed", "signed"]);
  if (status === "draft") return chain(["pending", "pending", "pending", "pending"]);
  if (status === "changes_requested" || status === "declined") {
    // Attribute the rejection to whoever actually made it. The old hardcoded second slot blamed
    // the Dept Director even when HR or the Executive Director was the rejecter.
    //
    // The stopping stage carries its own state per status: a red "declined" dot on a returned form
    // reads as "this hire is over" when the supervisor can still fix and re-submit it, which is the
    // same distinction the RETURNED chip makes (PR 30856 review, Pooja Pujara).
    const at = pendingStep(dto.latestSignatureRole, dto.latestSignatureSigned);
    const stopped: PifApprovalStepState = status === "changes_requested" ? "returned" : "declined";
    return chain(
      APPROVAL_STEP_LABELS.map<PifApprovalStepState>((_, index) =>
        index === at ? stopped : index < at ? "signed" : "pending",
      ),
    );
  }
  // submitted: manager (step 0) always signed; every stage before the pending one is signed too.
  const pending = pendingStep(dto.latestSignatureRole, dto.latestSignatureSigned);
  const states = APPROVAL_STEP_LABELS.map<PifApprovalStepState>((_, index) =>
    index === 0 || index < pending ? "signed" : "pending",
  );
  return chain(states);
};

const mapToPifListItem = (dto: PostIfListItemDto, currentUserId: number | null): PifListItem => ({
  id: String(dto.postIfId),
  title: dto.postIfTitle ?? dto.refNo ?? "",
  flow: mapFlow(dto.flow),
  applicant: dto.applicantName ?? "",
  createdBy: dto.createdByName ?? "",
  createdByUserId: dto.createdByUserId ?? undefined,
  department: dto.departmentName ?? "",
  position: dto.positionName ?? "",
  employmentType: mapEmploymentType(dto.employmentType),
  status: derivePifListStatus(dto),
  updated: formatUpdated(dto.updatedUtc ?? dto.createdUtc),
  employeeNumber: dto.enterpriseEmployeeId ? `#${dto.enterpriseEmployeeId}` : undefined,
  approvals: derivePifApprovals(dto),
  // "Mine" gates the edit/delete actions. The my-list also includes the caller's reports' forms,
  // so ownership is decided by creator id — not by which endpoint returned the row.
  isMine: dto.createdByUserId != null && dto.createdByUserId === currentUserId,
});

// First slice fetches a single page; server-side paging/search wiring is a follow-up that also
// touches the list component (it filters/searches client-side today).
const HR_LIST_PAGE_SIZE = 200;

export const getMyPostInterviewForms = async (currentUserId: number | null): Promise<PifListItem[]> => {
  // Bug 79824: my-list now answers { rows, total } rather than a bare array - reading it as an
  // array crashed the page outright ("...map is not a function"). It is asked for one large page
  // here on purpose: the list component still filters and searches the rows it holds, so a real
  // page size would silently narrow those filters to page 1. Same interim shape as the HR list
  // above; both switch to true server paging when the component does.
  const response = await apiClient.get<PostIfMyListResultDto>("/postif/my-list", {
    params: { pageNumber: 1, pageSize: HR_LIST_PAGE_SIZE },
  });
  return (response.data?.rows ?? []).map((row) => mapToPifListItem(row, currentUserId));
};

export const getPostInterviewForms = async (currentUserId: number | null): Promise<PifListItem[]> => {
  const response = await apiClient.get<PostIfHrListResultDto>("/postif/hr-list", {
    params: { pageNumber: 1, pageSize: HR_LIST_PAGE_SIZE },
  });
  return (response.data?.rows ?? []).map((row) => mapToPifListItem(row, currentUserId));
};

/* ── PIF list row actions (Epic 79822) ── */

// Soft-deletes a draft (creator-only; a submitted form is never deletable — enforced server-side).
export const softDeletePostIf = (postIfId: number): Promise<PostIfResultDto> =>
  readPostIfWrite(() => apiClient.post<PostIfResultDto>("/postif/soft-delete", { postIfId }));

// HR converts an approved PostIF into an employee record by linking its EnterpriseEmployeeId.
export const markPostIfAsEmployee = (params: {
  postIfId: number;
  enterpriseEmployeeId: number;
}): Promise<PostIfResultDto> =>
  readPostIfWrite(() =>
    apiClient.post<PostIfResultDto>("/postif/mark-as-employee", {
      postIfId: params.postIfId,
      enterpriseEmployeeId: params.enterpriseEmployeeId,
    }),
  );

/* ── PIF form reference data / lookups (Epic 79818) ── */
// The 7 dropdowns share the flat {id, text} shape; position-rules returns the band/classification
// metadata for a chosen department + position. All are GET, JWT-scoped (no nonce). These back the
// form's cascading selects; the component wiring that consumes them lands in the next sub-slice.

export const getPifDepartments = async (): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/departments");
  return response.data ?? [];
};

export const getPifPositions = async (departmentId: number): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/positions", {
    params: { departmentId },
  });
  return response.data ?? [];
};

export const getPifReportToUsers = async (departmentId: number): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/report-to-users", {
    params: { departmentId },
  });
  return response.data ?? [];
};

export const getPifSiteLocations = async (): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/site-locations");
  return response.data ?? [];
};

export const getPifServices = async (departmentId: number): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/services", {
    params: { departmentId },
  });
  return response.data ?? [];
};

export const getPifClients = async (serviceId: number): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/clients", {
    params: { serviceId },
  });
  return response.data ?? [];
};

export const getPifRateTypes = async (): Promise<PostIfLookupItem[]> => {
  const response = await apiClient.get<PostIfLookupItem[]>("/postif/rate-types");
  return response.data ?? [];
};

export const getPifPositionRules = async (
  departmentId: number,
  positionId: number,
): Promise<PostIfPositionRules> => {
  const response = await apiClient.get<PostIfPositionRules>("/postif/position-rules", {
    params: { departmentId, positionId },
  });
  return response.data;
};

// Adapts the API's position-rules to the form's internal `meta` shape, so the validation, pay-band,
// and offer-rail logic keeps reading `meta.cls/office/sched/band/hiring` unchanged. Null band
// bounds (no rate seeded yet) collapse to 0 — the pay band just shows no range for that position.
export const toPifPositionMeta = (rules: PostIfPositionRules | null): PifPositionMeta | null =>
  rules
    ? {
        cls: rules.classification ?? "",
        office: rules.isOffice,
        sched: rules.isScheduleBased,
        band: [rules.positionMin ?? 0, rules.positionMax ?? 0],
        hiring: [rules.hiringMin ?? 0, rules.hiringMax ?? 0],
      }
    : null;

/* ── PIF detail read + approval actions (Epic 79821 GET + Epic 79823) ── */

// GET /api/postif/{id} — the full form (header + applicant + EAV catalogue + signature timeline).
// Used to load a draft for editing and to build the approver's read-only review.
export const getPif = async (postIfId: number): Promise<PostIfDto> => {
  const response = await apiClient.get<PostIfDto>(`/postif/${postIfId}`);
  return response.data;
};

// GET /api/postif/approval-state — the signature timeline on its own (a lighter read than getPif).
export const getPifApprovalState = async (postIfId: number): Promise<PostIfSignatureDto[]> => {
  const response = await apiClient.get<PostIfSignatureDto[]>("/postif/approval-state", {
    params: { postIfId },
  });
  return response.data ?? [];
};

// Resolves an emailed approval deep-link token to its numeric PostIF id. 404 (unknown) and 410
// (expired) both collapse to null — the approval screen treats either as "this link can't be opened".
export const resolvePifApprovalToken = async (token: string): Promise<number | null> => {
  try {
    const response = await apiClient.get<{ postIfId: number }>(`/postif/resolve-token/${encodeURIComponent(token)}`);
    return response.data?.postIfId ?? null;
  } catch {
    return null;
  }
};

// POST /api/postif/approve — the current signer approves and advances the chain.
export const approvePifById = (postIfId: number): Promise<PostIfResultDto> =>
  readPostIfWrite(() => apiClient.post<PostIfResultDto>("/postif/approve", { postIfId }));

// POST /api/postif/reject — final=false returns the form for changes (re-submittable); final=true
// is a terminal decline.
export const rejectPifById = (params: {
  postIfId: number;
  reason: string;
  final: boolean;
}): Promise<PostIfResultDto> =>
  readPostIfWrite(() =>
    apiClient.post<PostIfResultDto>("/postif/reject", {
      postIfId: params.postIfId,
      reason: params.reason,
      final: params.final,
    }),
  );
